import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, UserCircle } from 'lucide-react';
import { representativeService } from '../services/representative.service';
import { messageHandler } from '../services/message-handler.service';
import type { Representative, RepresentativeFormData, RepresentativeFilters as Filters } from '../types/representative';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import RepresentativeList from '../components/representatives/RepresentativeList';
import RepresentativeForm from '../components/representatives/RepresentativeForm';
import RepresentativeFilters from '../components/representatives/RepresentativeFilters';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
}

function Representatives() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingRepresentative, setEditingRepresentative] = useState<Representative | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({});
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['representatives', currentPage, filters],
    queryFn: () => representativeService.getAll(currentPage, 20, filters),
  });

  const createMutation = useMutation({
    mutationFn: (data: RepresentativeFormData) => representativeService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['representatives'] });
      messageHandler.success('Vendedor criado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: RepresentativeFormData }) =>
      representativeService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['representatives'] });
      messageHandler.success('Vendedor atualizado com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => representativeService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['representatives'] });
      messageHandler.success('Vendedor excluído com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const phone = formData.get('phone') as string;
    const notes = formData.get('notes') as string;
    const integrationKeyStr = formData.get('integration_key') as string;
    const active = formData.get('active') === 'on';

    const data: RepresentativeFormData = {
      name,
      email: email || null,
      phone: phone || null,
      active,
      notes: notes || null,
      integration_key: integrationKeyStr ? parseInt(integrationKeyStr) : null,
    };

    if (editingRepresentative) {
      updateMutation.mutate({ id: editingRepresentative.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (representative: Representative) => {
    messageHandler.confirm(
      'Excluir Vendedor',
      'Tem certeza que deseja excluir este vendedor?',
      () => deleteMutation.mutate(representative.id)
    );
  };

  const handleFilter = (newFilters: Filters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingRepresentative(null);
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Vendedores"
          description="Gerencie os vendedores e suas informações de contato."
          icon={UserCircle}
          actions={
            <Button
              onClick={() => {
                setEditingRepresentative(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Vendedor
            </Button>
          }
        />

        <RepresentativeFilters onFilter={handleFilter} initialFilters={filters} />

        <RepresentativeList
          representatives={data?.data || []}
          onEdit={representative => {
            setEditingRepresentative(representative);
            setModal({ type: 'edit' });
          }}
          onDelete={handleDelete}
          isLoading={isLoading}
          pagination={data?.pagination}
          onPageChange={setCurrentPage}
        />

        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingRepresentative ? 'Editar Vendedor' : 'Novo Vendedor'}
        >
          <RepresentativeForm
            representative={editingRepresentative}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>
      </div>
    </div>
  );
}

export default Representatives;
